/** Display projection of a Last Update (Offering or Price — see CONTEXT.md):
 *  who changed a document and when. One shape for every surface that shows it
 *  (UPDATED column, quick preview) so they read alike. */
export interface LastUpdateView {
  /** "just now" / "5m ago" / "2h ago" / "6d ago" under 7 days, otherwise the
   *  absolute Bangkok date "20 Jun 2026, 14:30". "—" when unknown. */
  when: string;
  /** Email local part ("alice"), or the name as-is when it isn't an email.
   *  "—" when unknown. */
  who: string;
  /** Exact Bangkok time with offset and the full user, e.g.
   *  "27 Jun 2026, 14:30:05 (GMT+7) · alice@ais.co.th". "" when both unknown. */
  tooltip: string;
  /** True when neither the time nor the user is known. */
  missing: boolean;
  /** True when the stamp is within {@link RECENT_UPDATE_THRESHOLD_MS} of
   *  `now`: the row is highlighted so a just-saved price is easy to find
   *  again. */
  recent: boolean;
}

const DASH = "—";
const RELATIVE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** A Last Update younger than this is "recent" (highlighted). */
export const RECENT_UPDATE_THRESHOLD_MS = 15 * 60 * 1000;

/** Whether `ms` is within the recent window of `now`. Up to a minute of
 *  future clock drift still counts: the catalog and the browser clock are not
 *  the same clock. */
export function isRecentlyUpdated(ms: number, now: number): boolean {
  if (ms <= 0) return false;
  const diff = now - ms;
  return diff >= -60_000 && diff < RECENT_UPDATE_THRESHOLD_MS;
}

// Always Bangkok: the catalog stamps +07:00 and everyone must see one value,
// whatever zone their browser runs in. Parts are assembled by hand so the
// output doesn't drift with the runtime's ICU punctuation ("," vs " at ").
const BANGKOK_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Bangkok",
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function bangkokParts(ms: number): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of BANGKOK_PARTS.formatToParts(ms)) out[p.type] = p.value;
  return out;
}

/** Epoch millis of a Last Update stamp; 0 when empty, unparseable, or Go's
 *  zero time (`0001-01-01T00:00:00Z`, emitted when the backend can't parse
 *  the stored value). 0 also makes these rows sort last. */
export function lastUpdateMillis(at: string | null | undefined): number {
  if (!at) return 0;
  const ms = Date.parse(at);
  return Number.isNaN(ms) || ms <= 0 ? 0 : ms;
}

function relative(ms: number, now: number): string {
  const diff = Math.max(0, now - ms);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Format a Last Update for display. `now` is injectable for tests; the list
 *  computes it once per load (relative times refresh on reload, not on a
 *  timer). */
export function formatLastUpdate(
  at: string | null | undefined,
  by: string | null | undefined,
  now: number = Date.now(),
): LastUpdateView {
  const ms = lastUpdateMillis(at);
  const user = by?.trim() ?? "";

  let when = DASH;
  let exact = "";
  if (ms) {
    const p = bangkokParts(ms);
    const date = `${p["day"]} ${p["month"]} ${p["year"]}`;
    exact = `${date}, ${p["hour"]}:${p["minute"]}:${p["second"]} (GMT+7)`;
    when =
      now - ms < RELATIVE_WINDOW_MS
        ? relative(ms, now)
        : `${date}, ${p["hour"]}:${p["minute"]}`;
  }

  const atSign = user.indexOf("@");
  const who = user ? (atSign > 0 ? user.slice(0, atSign) : user) : DASH;

  return {
    when,
    who,
    tooltip: [exact, user].filter(Boolean).join(" · "),
    missing: !ms && !user,
    recent: isRecentlyUpdated(ms, now),
  };
}
